import { format, parseISO } from "date-fns";
import { IconButton, Chip } from "@mui/material";
import { Download, Visibility, Cancel, Upload } from "@mui/icons-material";
import Swal from "sweetalert2";
import i18next from "i18next";

import { downloadFile, showFile } from "@shared/api/axiosClient";
import { ErrorAlert, ErrorServer, InfoAlert, SuccessAlert } from "@shared/ui/alerts";
import { dollarUS } from "@shared/utils/moneyFormat";
import {
  types,
  subType,
  statusPreInvoice,
  statusForInvoice,
} from "@modules/invoice";

import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

import {
  cancelPreInvoice,
  confirmPreInvoice,
  getInvoiceSubmissions,
  getPendingPreInvoices,
  getPreInvoices,
  sendInvoice,
} from "../api/adminApi";
import { ADMIN_ACTION, ADMIN_MODULE, DOC_STATUS, HTTP_STATUS, SEND_CHIP_COLOR, SWAL_COLOR } from "../constants";

import type { AdminAction, AdminModule } from "../constants";

interface AdminRowDetail {
  client?: string;
  description?: string;
  modalityWork?: string;
  priceUnit?: number;
}

/**
 * Fila de la grilla del panel. La misma tabla muestra facturas, envios de factura y
 * prefacturas — tres DTOs distintos, con las columnas elegidas por modulo en tiempo de
 * ejecucion — asi que se declaran los campos que el componente realmente lee y se deja una
 * firma de indice para el resto.
 */
interface AdminRow {
  id?: number;
  consultantName?: string;
  status?: string;
  invoice?: { id?: number; status?: string } | null;
  details?: AdminRowDetail[];
  [key: string]: unknown;
}

export interface AdminActionResult {
  data: AdminRow[];
  columns: GridColDef[];
  title: string;
}

const ExecutorActions = (
  action: AdminAction,
  module: AdminModule,
  thisLoading: () => void,
  notLoading: () => void,
  redmineId: number,
): Promise<AdminActionResult | undefined> | undefined => {
  const onDownload = (id: number, consultantName: string, type: AdminModule, isInvoice = false) => {
    thisLoading();
    if (type === ADMIN_MODULE.PRE_INVOICES) {
      downloadFile(
        `admin/pre-invoice/${redmineId}/${id}/download`,
        `PreFactura_${format(new Date(), "dd_MM_yyyy_HH:mm:ss")}.pdf`
      ).finally(() => notLoading());
    }
    if (type === ADMIN_MODULE.INVOICES) {
      const url = isInvoice
        ? `admin/invoice/${id}/download`
        : `admin/invoice-submission/${id}/download`;
      downloadFile(
        url,
        `Factura_${consultantName}_${format(new Date(), "dd_MM_yyyy_HH:mm:ss")}.pdf`
      ).finally(() => notLoading());
    }
  };

  const onView = (id: number, isInvoice: boolean) => {
    thisLoading();
    const url = isInvoice
      ? `admin/invoice/${id}/download`
      : `admin/invoice-submission/${id}/download`;
    showFile(url)
      .catch(() => {
        ErrorServer().then();
      })
      .finally(() => notLoading());
  };

  // Pide el email de copia y envía la factura.
  const onSend = async (invoiceId: number) => {
    const { value: email } = await Swal.fire({
      icon: "info",
      title: i18next.t("billShipping"),
      input: "email",
      inputLabel: i18next.t("copyEmail"),
      inputPlaceholder: i18next.t("enterCopyEmail"),
      showCancelButton: true,
      confirmButtonColor: SWAL_COLOR.CONFIRM,
      cancelButtonColor: SWAL_COLOR.CANCEL,
      confirmButtonText: i18next.t("sentInvoice"),
      cancelButtonText: i18next.t("cancel"),
    });

    if (!email) return;
    thisLoading();
    sendInvoice(redmineId, { ccEmail: email, invoiceId })
      .then(() => {
        notLoading();
        SuccessAlert(i18next.t("billShipping"), i18next.t("doneSuccessfully", { a: "a" })).then();
      })
      .catch((error: { response?: { data?: { message?: string; code?: number } } }) => {
        const data = error.response?.data;
        notLoading();
        ErrorAlert(
          i18next.t("errorSendingInvoice"),
          data?.message ?? i18next.t("internalError")
        ).then();
      });
  };

  const columns: GridColDef[] = [
    {
      field: "consultantName",
      headerName: i18next.t("collaborator"),
      flex: 1,
    },
    {
      field: "invoiceNumber",
      headerName: i18next.t("reference"),
      flex: 1,
    },
    {
      field: "status",
      headerName: i18next.t("status"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<AdminRow>) =>
        statusForInvoice(params.row.invoice ? params.row.invoice.status : params.value, i18next.t),
    },
    {
      field: "types",
      headerName: i18next.t("type"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<AdminRow>) => types(params.value, i18next.t),
    },
    {
      field: "subTypes",
      headerName: i18next.t("subtype"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<AdminRow>) => subType(params.value, i18next.t),
    },
    {
      field: "rejectReason",
      headerName: i18next.t("rejectReason"),
      flex: 1,
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
      valueFormatter: (value) => dollarUS.format(value),
    },
    {
      field: "sentDate",
      headerName: i18next.t("date"),
      flex: 1,
      valueFormatter: (value, row) =>
        row.invoice
          ? format(parseISO(row.invoice.createdAt), "dd/MM/yyyy")
          : format(parseISO(value), "dd/MM/yyyy"),
    },
    {
      field: "send",
      headerName: i18next.t("send"),
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<AdminRow>) => {
        const isInvoice = !!params.row.invoice;
        const invoiceId = isInvoice ? params.row.invoice?.id : params.row.id;
        if (!isInvoice) {
          return "";
        }
        if (invoiceId && params.row.invoice?.status !== DOC_STATUS.CREATED) {
          return "";
        }

        return (
          <Chip
            icon={<Upload style={{ color: "white" }} />}
            label={i18next.t("send")}
            sx={{ backgroundColor: SEND_CHIP_COLOR, color: "#fff" }}
            onClick={() => {
              onSend(invoiceId as number);
            }}
          />
        );
      },
    },
    {
      field: "see",
      headerName: i18next.t("see"),
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<AdminRow>) => {
        const isInvoice = !!params.row.invoice;
        const invoiceId = isInvoice ? params.row.invoice?.id : params.row.id;
        return (
          <IconButton onClick={() => onView(invoiceId as number, isInvoice)}>
            <Visibility />
          </IconButton>
        );
      },
    },
    {
      field: "download",
      headerName: i18next.t("download"),
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<AdminRow>) => {
        const isInvoice = !!params.row.invoice;
        const invoiceId = isInvoice ? params.row.invoice?.id : params.row.id;
        return (
          <IconButton
            onClick={() =>
              onDownload(invoiceId as number, params.row.consultantName ?? "", ADMIN_MODULE.INVOICES, isInvoice)
            }
          >
            <Download />
          </IconButton>
        );
      },
    },
  ];

  const columnsPreInvoice = [
    {
      field: "createdAt",
      headerName: i18next.t("createdAt"),
      flex: 1,
    },
    {
      field: "detailsClient",
      headerName: i18next.t("client"),
      flex: 1,
      valueGetter: (_value: unknown, row: AdminRow) =>
        (row.details ?? []).map((item) => item.client).join(",") + " ",
    },
    {
      field: "detailsDescription",
      headerName: i18next.t("description"),
      flex: 1,
      valueGetter: (_value: unknown, row: AdminRow) =>
        (row.details ?? []).map((item) => item.description).join(",") + " ",
    },
    {
      field: "detailsModalityWork",
      headerName: i18next.t("modalityWork"),
      flex: 1,
      valueGetter: (_value: unknown, row: AdminRow) =>
        (row.details ?? []).map((item) => item.modalityWork).join(",") + " ",
    },
    {
      field: "detailsUnitPrice",
      headerName: i18next.t("unitPrice"),
      flex: 1,
      valueGetter: (_value: unknown, row: AdminRow) =>
        (row.details ?? []).map((item) => item.priceUnit).join(",") + " ",
    },
    {
      field: "hours",
      headerName: i18next.t("hours"),
      flex: 1,
    },
    {
      field: "month",
      headerName: i18next.t("month"),
      flex: 1,
    },
    {
      field: "year",
      headerName: i18next.t("year"),
      flex: 1,
    },
    {
      field: "benefitDiscount",
      headerName: i18next.t("discount"),
      flex: 1,
    },
    {
      field: "total",
      headerName: i18next.t("subTotal"),
      flex: 1,
    },
    {
      field: "currency",
      headerName: i18next.t("currency"),
      flex: 1,
    },
    {
      field: "status",
      headerName: i18next.t("status"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<AdminRow>) => statusPreInvoice(params.value, i18next.t),
    },
    {
      field: "Descargar",
      headerName: i18next.t("download"),
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<AdminRow>) => (
        <IconButton
          onClick={() => {
            onDownload(params.row.id as number, "", ADMIN_MODULE.PRE_INVOICES);
          }}
        >
          <Download />
        </IconButton>
      ),
    },
    {
      field: "Cancelar",
      headerName: i18next.t("cancel"),
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<AdminRow>) => {
        if (params.row.status !== DOC_STATUS.CREATED) {
          return "";
        }
        return (
          <IconButton
            onClick={() => {
              onCancelPreInvoice(params.row.id as number, params.row.status);
            }}
          >
            <Cancel />
          </IconButton>
        );
      },
    },
  ];

  const onCancelPreInvoice = (preInvoiceId: number, status?: string) => {
    if (status !== DOC_STATUS.CANCELLED) {
      Swal.fire({
        title: i18next.t("cancelledPreInvoiceTitle"),
        text: i18next.t("cancelledPreInvoiceQuestion"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: i18next.t("cancel"),
        cancelButtonText: i18next.t("exit"),
      }).then((result) => {
        if (result.isConfirmed) {
          thisLoading();
          cancelPreInvoice(preInvoiceId)
            .then(() => {
              notLoading();
              Swal.fire({
                title: i18next.t("successfulCancellationTitle"),
                text: i18next.t("successfulCancellationMessage"),
                icon: "success",
                confirmButtonText: "OK",
              });
            })
            .catch((error: { response?: { data?: { message?: string; code?: number } } }) => {
              console.log("error => ", error);
              Swal.fire({
                title: i18next.t("cancelledPreInvoiceTitle"),
                text: i18next.t("errorCancelledPreInvoice"),
                icon: "error",
                confirmButtonText: "OK",
              });
            })
            .finally(() => {
              notLoading();
            });
        }
      });
    } else {
      Swal.fire({
        title: i18next.t("cancelInvoice"),
        text: i18next.t("invoiceIsAlreadyCanceled"),
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleSuccess = (data: AdminRow[], columnsDef: GridColDef[], title: string): AdminActionResult => {
    return { data, columns: columnsDef, title };
  };

  const onGenerate = (redmineUserId: number) => {
    thisLoading();
    getPendingPreInvoices(redmineUserId)
      .then((data) => {
        if (!Array.isArray(data) || data.length === 0) {
          notLoading();
          InfoAlert(i18next.t("generateInvoice"), i18next.t("noPreInvoicesAvailable")).then();
          return;
        }
        // El `if` de arriba descarta el array vacio.
        confirmPreInvoice(redmineUserId, data[0]!.id as number)
          .then(() => {
            notLoading();
            SuccessAlert(
              i18next.t("invoiceGeneration"),
              i18next.t("doneSuccessfully", { a: "a" })
            ).then();
          })
          .catch((error: { response?: { data?: { message?: string; code?: number } } }) => {
            notLoading();
            const errorData = error.response?.data;
            if (errorData?.code === HTTP_STATUS.BAD_REQUEST) {
              ErrorAlert(
                i18next.t("errorToGenerateInvoiceTitle"),
                errorData?.message ?? i18next.t("errorToGenerateInvoice")
              ).then();
              return;
            }
            ErrorAlert("Error", i18next.t("errorToGenerateInvoice")).then();
          });
      })
      .catch((error: { response?: { data?: { message?: string; code?: number } } }) => {
        notLoading();
        const data = error.response?.data;
        if (data?.code === HTTP_STATUS.BAD_REQUEST) {
          ErrorAlert(
            i18next.t("errorToGenerateInvoiceTitle"),
            data?.message ?? i18next.t("errorToGenerateInvoice")
          ).then();
          return;
        }
        ErrorAlert("Error", i18next.t("errorToGenerateInvoice")).then();
      });
  };

  const handleError = (error: unknown): never => {
    ErrorAlert("Error", i18next.t("internalError")).then();
    throw error;
  };

  // Promesa vacía para acciones sin efecto de datos (la UI solo refresca estado).
  // Resuelve con un placeholder que ningun consumidor lee: las ramas que usan noop
  // (GENERATE / SEND) no renderizan `info`. Se conserva el valor tal cual estaba.
  const noop = (): Promise<AdminActionResult | undefined> =>
    new Promise((resolve) => setTimeout(() => resolve("foo" as unknown as AdminActionResult), 0));

  switch (module) {
    case ADMIN_MODULE.INVOICES:
      switch (action) {
        case ADMIN_ACTION.CONSULT:
          return getInvoiceSubmissions(redmineId)
            .then((data) => handleSuccess(data as AdminRow[], columns, i18next.t("invoicesSent")))
            .catch(handleError);
        case ADMIN_ACTION.GENERATE:
          thisLoading();
          onGenerate(redmineId);
          return noop();
        case ADMIN_ACTION.SEND:
          return noop();
        default:
          console.error(`Acción "${action}" no implementada en módulo ADMIN_MODULE.INVOICES`);
          break;
      }
      break;
    case ADMIN_MODULE.PRE_INVOICES:
      switch (action) {
        case ADMIN_ACTION.CONSULT:
          return getPreInvoices(redmineId)
            .then((data) => handleSuccess(data as AdminRow[], columnsPreInvoice, i18next.t("preInvoicesSent")))
            .catch(handleError);
        case ADMIN_ACTION.GENERATE:
          return noop();
        default:
          console.error(`Acción "${action}" no implementada en módulo ADMIN_MODULE.PRE_INVOICES`);
          break;
      }
      break;
    default:
      console.error(`Módulo "${module}" no encontrado`);
      break;
  }
};

export { ExecutorActions };
